import { useState, useEffect } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { Employee } from '../../axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from 'common/errorHandler';
import {
	Typography,
	Button,
	Box,
	Card,
	Checkbox,
	FormControlLabel,
  } from '@mui/material';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import { DEFAULT_PAGINATION, useSearch } from 'hooks/useSearch';
import SearchInput from 'components/SearchInput';
import { getUserEntity } from 'common/UserEntityProvider';
import { SCOPES } from 'common/permissions';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { useNavigate } from 'react-router';


export type StateInput = {
    queryTerm?: string | null,
    sortValue?: string | null,
    orderValue?: string | null,
    filterValue?: null,
    currentPage?: number,
    clicked?: boolean,
};

export type SearchResult ={
    totalRecords?: number | null,
    employees?: Employee[]
};

const Employees = () => {
	const navigate = useNavigate();
    const api: Api = useApi();

	const entityType = getUserEntity();
	
	const [errors, setErrors] = useState({});
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [inactiveEmployees, setInactiveEmployees] = useState<boolean>(false);

    const toast = useToastMessageQueue();

	const apiPromise = (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string
  	) => {
  	  return api.employeeApi.apiVversionEmployeeSearchGet("1", searchTerm, pageNum, pageSize, sort, order, inactiveEmployees);
  	};

	const processError = (error: any) => {
	handleAPIError(error, toast, errors);
	setErrors({ ...errors });
	};

	const { data, loading, onPaginationModelChange, handleSortModelChange, reFetchData } = useSearch<Employee>({
		apiPromise: apiPromise,
		searchTerm: searchTerm,
		defaultSort: { sort: 'Name', order: 'asc' },
		onError: processError,
	});


	const handleClearSearch = () => {
    	setSearchTerm('');
  	};

	const handleInactiveEmployees = () => {
		setInactiveEmployees(!inactiveEmployees);
	};

	useEffect(() => {
		reFetchData();
	}, [inactiveEmployees]);

	const INITIAL_STATE: GridInitialState = {
		pagination: {
		  paginationModel: {
			page: DEFAULT_PAGINATION.pageNum,
			pageSize: DEFAULT_PAGINATION.pageSize,
		  },
		},
		sorting: {
		  sortModel: [
			{
			  field: 'Name',
			  sort: 'asc',
			},
		  ],
		},
	  };


	const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: intl.get('employeeList.table.thead.name'),
      width: 250,
      flex: 1,
    },
	{
      field: 'lastname',
      headerName: intl.get('employeeList.table.thead.lastName'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{params.row.lastName || ''}</>,
    },
    {
      field: 'email',
      headerName: intl.get('employeeList.table.thead.email'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{params.row.emailAddress || ''}</>,
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={entityType.entityType}
          onEdit={() => navigate(`/employee/detail/${params.row.id}`)}
          editScopes={[SCOPES['employees.edit']]}
        />
      ),
    },
  ];

	return (
		<Card sx={{ m: 2 }}>
        <Box sx={{ p: 3 }}>
      		<Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
      		  {intl.get('employee.header')}
      		</Typography>
			<Box sx={{ p: 3 }}>
				<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
				<Box display={'flex'} alignItems={'center'}>
				<SearchInput value={searchTerm} onChange={setSearchTerm} onClear={handleClearSearch} />
				<FormControlLabel
						control={
							<Checkbox
								checked={inactiveEmployees}
								onChange={handleInactiveEmployees}
								sx={{ ml: 1 }}
							/>
						}
						label={intl.get('employeeList.checkbox.inactive')}
					/>
				</Box>
				<Button onClick={() => navigate(`/employee/new`)}>
					<AddIcon /> {intl.get('employee.new') ? intl.get('employee.new') : intl.get('button.new')}
				</Button>
				</Box>
				<Box style={{ width: '100%', display: 'flex' }}>
				{loading && !data && <Spinner />}
				{data && (
					<DataGrid
					rows={data.queryResult || []}
					columns={columns}
					rowCount={data.totalRecords || 0}
					loading={loading}
					initialState={INITIAL_STATE}
					onPaginationModelChange={onPaginationModelChange}
					onSortModelChange={handleSortModelChange}
					getRowClassName={(params) =>
						!params.row.isActive ? 'employee-inactive' : ''
					}
					sx={{
						'& .employee-inactive': {
							backgroundColor: 'rgba(0, 0, 0, 0.03)',
						},
					}}
					/>
				)}
				</Box>
			</Box>
    	</Box>
		</Card>
    );
}

export default Employees;